import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import toast from 'react-hot-toast';
import {
  Save,
  ArrowLeft,
  Eye,
  RefreshCw
} from 'lucide-react';
import EntityBrowser from './components/EntityBrowser';
import FieldBrowser from './components/FieldBrowser';
import StatCardEditor from './components/StatCardEditor';
import TableEditor from './components/TableEditor';
import ChartEditor from './components/ChartEditor';
import PreviewPanel from './components/PreviewPanel';

const VisualizationBuilder = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const navigate = useNavigate();

  const mode = id ? 'edit' : 'create';
  const initialType = searchParams.get('type') || 'StatCard';

  const [componentType, setComponentType] = useState(initialType);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entityName, setEntityName] = useState('');
  const [config, setConfig] = useState({});
  const [gridSize, setGridSize] = useState({ width: 1, height: 1 }); // Grid size in columns x rows
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadComponent();
    }
  }, [mode, id]);

  const loadComponent = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load component');
      }

      const data = await response.json();
      const component = data.data;

      setComponentType(component.ComponentType);
      setName(component.Name);
      setDescription(component.Description || '');
      setEntityName(component.EntityName);
      setConfig(component.config || {});
      // Load grid size from config if available, otherwise use defaults
      if (component.config?.gridSize) {
        setGridSize(component.config.gridSize);
      } else {
        // Set default grid size based on component type
        const defaultSizes = {
          'StatCard': { width: 1, height: 1 },
          'Table': { width: 4, height: 2 },
          'BarChart': { width: 2, height: 2 },
          'LineChart': { width: 2, height: 2 },
          'PieChart': { width: 2, height: 2 }
        };
        setGridSize(defaultSizes[component.ComponentType] || { width: 1, height: 1 });
      }
    } catch (error) {
      console.error('Error loading component:', error);
      toast.error('Failed to load component');
      navigate('/visualization-studio');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!name.trim()) {
        toast.error('Please enter a component name');
        return;
      }

      if (!entityName) {
        toast.error('Please select an entity');
        return;
      }

      // Validate config based on type
      if (componentType === 'StatCard') {
        if (!config.aggregation || !config.aggregation.function) {
          toast.error('Please configure the stat card aggregation');
          return;
        }
        // If SUM is selected, a field must be chosen
        if (config.aggregation.function === 'SUM' && !config.aggregation.field) {
          toast.error('Please select a field to sum');
          return;
        }
      }

      if (componentType === 'Table' && (!config.fields || config.fields.length === 0)) {
        toast.error('Please select at least one field for the table');
        return;
      }

      // Validate chart configuration
      if (['BarChart', 'LineChart', 'PieChart'].includes(componentType)) {
        const isPieChart = componentType === 'PieChart';
        
        // Pie charts need both category field (xAxis) and value field (series)
        if (isPieChart) {
          if (!config.xAxis) {
            toast.error('Please select a category field for the pie chart');
            return;
          }
        } else {
          // Bar/Line charts need X-axis
          if (!config.xAxis) {
            toast.error('Please select an X-axis field for the chart');
            return;
          }
        }
        
        if (!config.series || config.series.length === 0) {
          toast.error('Please add at least one data series');
          return;
        }
        
        // Validate each series has a field
        for (const series of config.series) {
          if (!series.field) {
            toast.error('Please select a field for all series');
            return;
          }
        }
      }

      setSaving(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const componentData = {
        componentType,
        name,
        description,
        entityName,
        config: {
          ...config,
          gridSize // Include grid size in config
        }
      };

      const url = mode === 'edit' 
        ? `/api/visualization-components/${id}` 
        : '/api/visualization-components';
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(componentData)
      });

      if (!response.ok) {
        throw new Error('Failed to save component');
      }

      toast.success(mode === 'edit' ? 'Component updated successfully' : 'Component created successfully');
      navigate('/visualization-studio');
    } catch (error) {
      console.error('Error saving component:', error);
      toast.error('Failed to save component');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (!entityName || !config) {
      return;
    }

    try {
      setPreviewLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Create a temporary component for preview
      const tempComponent = {
        ComponentType: componentType,
        ConfigJSON: JSON.stringify({
          ...config,
          entityName
        })
      };

      // For now, we'll use a direct execution approach
      // In production, you might want to create a preview endpoint
      const response = await fetch('/api/visualization-components/preview', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          componentType,
          entityName,
          config
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load preview');
      }

      const data = await response.json();
      setPreviewData(data.data);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading component...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => navigate('/visualization-studio')}
              className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Component Name"
                className="w-full px-3 py-2 text-lg font-semibold border-b-2 border-transparent focus:border-white/50 bg-transparent outline-none text-white placeholder-teal-200 focus:bg-white/10 rounded transition-colors"
              />
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                className="w-full px-3 py-1 text-sm text-teal-100 placeholder-teal-200 border-b border-transparent focus:border-white/50 bg-transparent outline-none focus:bg-white/10 rounded transition-colors"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshPreview}
              disabled={!entityName || previewLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${previewLoading ? 'animate-spin' : ''}`} />
              Preview
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-teal-700 font-medium hover:bg-teal-50 shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Configuration */}
        <div className="w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Component Type (only in create mode) */}
            {mode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Component Type
                </label>
                <select
                  value={componentType}
                  onChange={(e) => {
                    setComponentType(e.target.value);
                    // Set default grid size based on component type
                    const defaultSizes = {
                      'StatCard': { width: 1, height: 1 },
                      'Table': { width: 4, height: 2 },
                      'BarChart': { width: 2, height: 2 },
                      'LineChart': { width: 2, height: 2 },
                      'PieChart': { width: 2, height: 2 }
                    };
                    setGridSize(defaultSizes[e.target.value] || { width: 1, height: 1 });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="StatCard">Stat Card</option>
                  <option value="Table">Table</option>
                  <option value="BarChart">Bar Chart</option>
                  <option value="LineChart">Line Chart</option>
                  <option value="PieChart">Pie Chart</option>
                </select>
              </div>
            )}

            {/* Grid Size Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Grid Size (Columns × Rows)
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { width: 1, height: 1, label: '1×1' },
                  { width: 1, height: 2, label: '1×2' },
                  { width: 1, height: 4, label: '1×4' },
                  { width: 2, height: 2, label: '2×2' },
                  { width: 4, height: 4, label: '4×4' },
                  { width: 2, height: 1, label: '2×1' },
                  { width: 4, height: 1, label: '4×1' },
                  { width: 2, height: 4, label: '2×4' },
                  { width: 4, height: 2, label: '4×2' },
                  { width: 3, height: 2, label: '3×2' }
                ].map((size) => (
                  <button
                    key={`${size.width}x${size.height}`}
                    type="button"
                    onClick={() => setGridSize({ width: size.width, height: size.height })}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                      gridSize.width === size.width && gridSize.height === size.height
                        ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-teal-400 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
                    }`}
                    title={`${size.width} column${size.width > 1 ? 's' : ''} × ${size.height} row${size.height > 1 ? 's' : ''}`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Dashboard uses a 4-column grid. Selected: {gridSize.width}×{gridSize.height}
              </p>
            </div>

            {/* Entity Browser */}
            <EntityBrowser
              value={entityName}
              onChange={setEntityName}
            />

            {/* Type-specific Editor */}
            {entityName && (
              <>
                {componentType === 'StatCard' && (
                  <StatCardEditor
                    entityName={entityName}
                    config={config}
                    onChange={setConfig}
                  />
                )}

                {componentType === 'Table' && (
                  <TableEditor
                    entityName={entityName}
                    config={config}
                    onChange={setConfig}
                  />
                )}

                {['BarChart', 'LineChart', 'PieChart'].includes(componentType) && (
                  <ChartEditor
                    entityName={entityName}
                    config={config}
                    onChange={setConfig}
                    componentType={componentType}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 overflow-y-auto p-6">
          <PreviewPanel
            componentType={componentType}
            entityName={entityName}
            config={config}
            data={previewData}
            loading={previewLoading}
            onRefresh={handleRefreshPreview}
          />
        </div>
      </div>
    </div>
  );
};

export default VisualizationBuilder;

